import { Component, computed, inject, input, output } from '@angular/core';
import { Store } from '@ngrx/store';
import { selectDisablePaymentsIsEnabled } from '../../../../store/selectors/feature-flag.selector';
import {
  AutoPayEnrollmentDisplay,
  AutoPayResponse,
  PaymentNotification,
} from '../../../accounts-notifications/models/accounts-notifications.model';
import { LoanAccount } from '../../models/accounts.model';
import { EventTrackingService } from '../../../../services/event-tracking.service';
import { LinkClick } from '../../../../models/event-data.model';
import { AuthEventType } from '../../../../enums/event/event.enum';
import { AuthLinkType } from '../../../../enums/event/event-data.enum';

@Component({
  selector: 'app-loan-account-item',
  templateUrl: './loan-account-item.component.html',
  styleUrls: ['./loan-account-item.component.scss'],
  standalone: false,
})
export class LoanAccountItemComponent {
  readonly account = input.required<LoanAccount>();
  readonly baseUrl = input<string>('');
  readonly autoPay = input<AutoPayResponse | undefined>(undefined);
  readonly onClickLoanCard = output<LoanAccount>();
  readonly onClickLoanMakePayment = output<LoanAccount>();
  readonly onClickChangeDueDate = output<LoanAccount>();

  private readonly store = inject(Store);
  private readonly eventTrackingService = inject(EventTrackingService);

  readonly isPaymentsDisabled$ = this.store.select(selectDisablePaymentsIsEnabled);

  readonly loanAccountTestId = computed(() => `loan-account-${this.account()?.last4DigitsExternalAccountNumber}`);
  readonly nextPaymentAmountDue = computed(() => this.getNextPaymentAmountDue(this.account()));
  readonly accountTitle = computed(() => {
    const loanAccount = this.account();
    return `${this.getProductType(loanAccount.productType)}...${loanAccount.last4DigitsExternalAccountNumber}`;
  });
  readonly dueDateIsClose = computed(() => {
    const loanAccount = this.account();
    const today = new Date();
    const dueDate = new Date(loanAccount.nextDueDate);
    const diffInTime = dueDate.getTime() - today.getTime();
    const diffInDays = diffInTime / (1000 * 60 * 60 * 24);
    return diffInDays >= 0 && diffInDays <= 5;
  });

  readonly autoPayEnrollmentDisplay = computed((): AutoPayEnrollmentDisplay | undefined => {
    const autoPay = this.autoPay();
    if (autoPay === undefined) {
      return undefined;
    }
    return this.autoPayResponseToEnrollmentDisplay(autoPay) ?? { kind: 'notEnrolled' };
  });

  readonly accountNotifications = computed((): PaymentNotification[] => {
    const autoPay = this.autoPay();
    if (autoPay === undefined) {
      return [];
    }
    return autoPay.paymentNotifications ?? [];
  });

  emitLoanClick() {
    this.onClickLoanCard.emit(this.account());
  }

  emitLoanMakePaymentClick() {
    this.onClickLoanMakePayment.emit(this.account());
  }

  private findAutoPayOtpDelayNotification(
    notifications: PaymentNotification[] | undefined | null
  ): PaymentNotification | undefined {
    if (!notifications?.length) {
      return undefined;
    }
    return notifications.find((notification) => {
      if (notification.autoPayType !== 'SINGLEPAYMENT') {
        return false;
      }
      const paymentConfirmation = notification.paymentConfirmation;
      if (paymentConfirmation == null) {
        return false;
      }
      return paymentConfirmation.startsWith('CWAOTP') || paymentConfirmation.startsWith('CWDOTP');
    });
  }

  private autoPayResponseToEnrollmentDisplay(response: AutoPayResponse): AutoPayEnrollmentDisplay | undefined {
    const isEnrolled = response.autoPayEligibilityState?.isEnrolled;
    if (isEnrolled == null) {
      return undefined;
    }
    if (!isEnrolled) {
      return { kind: 'notEnrolled' };
    }
    const otpDelayNotification = this.findAutoPayOtpDelayNotification(response.paymentNotifications);
    if (otpDelayNotification) {
      return { kind: 'skipped', resumesOnIso: response.autoPayDetail?.autoPayItems?.[0]?.scheduledDate ?? '' };
    }
    return { kind: 'enrolled', frequency: response.autoPayFrequency ?? '' };
  }

  private getProductType(productType: string | undefined): string {
    if (!productType?.length || productType.length === 0) {
      return 'Personal loan';
    }
    return productType.charAt(0) + productType.slice(1).toLowerCase();
  }

  private getNextPaymentAmountDue(loanAccount: LoanAccount): number {
    if (loanAccount.accountStatus === 'Past Due') {
      return loanAccount.autoPayStatus === 'Enrolled'
        ? loanAccount.pastDueAmount
        : loanAccount.calculatedTotalAmountDue;
    }
    return loanAccount.nextPaymentAmountDue;
  }

  emitChangeDueDateClick() {
    this.onClickChangeDueDate.emit(this.account());
    this.eventTrackingService.trackEvent<LinkClick>(AuthEventType.LINK_CLICK, {
      authClickLinkText: 'Change Due Date',
      authClickLinkType: AuthLinkType.NAV_TO_CHANGE_DUE_DATE,
    });
  }
}
